import { Subject, Subscription } from 'rxjs';

import { Dimension2D } from '../../../../types/geo.types';
import { HTMLUtils } from '../../../../utils/html.utils';
import { isEqual, keys, pick } from '../../../../utils/lodash-min';
import { RxUtils } from '../../../../utils/rx-utils';
import { AbstractEditTool } from './abstract-edit-tool';
import { CCanvas } from './ccanvas';
import { ImageContainer } from './image-container';
import { Photo } from './photo';
import { ResizableContainerBounds, ResizableContainerBoundsChangeEvent, UIResizeSquare } from './resizable_square';

export class Resize extends AbstractEditTool {
    photoModel: Photo;
    image_url = '';
    isImageReady = false;
    mrs: UIResizeSquare = null;
    listeners = new Array();
    events = new Array();
    image_width;
    image_height;
    resizeElement: HTMLDivElement;

    valueChanges: Subject<ResizableContainerBoundsChangeEvent>;
    resizeFormGroup: any; // FormGroup; // TODO Preact need to fix this, ui resize presets not gonna work
    originalDim: ResizableContainerBounds;
    subscription: Subscription;

    constructor(photoModel: Photo, container: ImageContainer) {
        super(container);
        this.photoModel = photoModel;
        this.valueChanges = new Subject();
    }

    public async load(options?: any) {
        console.time('load time');
        this.resizeFormGroup = options.resizeFormGroup;

        let rs = new UIResizeSquare(this.container, this.container.getElement());
        this.mrs = rs;
        rs.init(this.valueChanges);
        this.handleResizeEvent();
        this.showOverlay();
        this.container.notify('resize');
        this.fitMovableResizableSqureToCanvas();
        this.mrs.update_faded_area();
        this.updateOverlayCanvas();
        this.originalDim = {
            top: 0,
            left: 0,
            width: this.mrs.getResizableElement().getBoundingClientRect().width,
            height: this.mrs.getResizableElement().getBoundingClientRect().height,
            top_percentage: 0,
            left_percentage: 0,
            width_percentage: 100,
            height_percentage: 100,
        }
        this.mrs.setDim(this.originalDim);
        this.updateOriginalImageSize();

        this.subscription = this.valueChanges.subscribe({
            next: value => {
                const dim = this.mrs.getDim();
                const originalDim = pick(this.originalDim, keys(dim));
                const changed = !isEqual(originalDim, dim);
                this.notifyHasChanges(changed);
            }
        });
        this.valueChanges.next(Object.assign({}, this.mrs.getDim(), { isTrusted: false }));
        console.timeEnd('load time');
    }

    private showImage() {
        // HTMLUtils.css(this.getContainer().getImage(), 'display', `block`);
    }

    private updateOverlayCanvas() {
        let canvas = this.mrs.getOverlayCanvas();
        HTMLUtils.css(canvas, 'display', `block`);
        HTMLUtils.css(canvas, 'width', `100%`);
        HTMLUtils.css(canvas, 'height', `100%`);
        CCanvas.copyCanvas(this.photoModel.getEphemeralCanvas(), canvas);
        HTMLUtils.addClass(this.photoModel.getEphemeralCanvas(), 'relative');
    }

    public resizeMouseDown(e) {
        this.mrs.resizeMouseDown(e);
    }

    public mouseMove(e) {
        try {
            this.mrs.mouseMove(e);
            if (e.stopPropagation) { e.stopPropagation(); }
            if (e.preventDefault) { e.preventDefault(); }
            e.cancelBubble = true;
            e.returnValue = false;
        } catch (e) {
            console.log(e)
        }
        return false;
    }

    public fitMovableResizableSqureToCanvas() {
        const canvas = this.photoModel.getEphemeralCanvas();
        this.mrs.getResizableElement().style.top = '0%';
        this.mrs.getResizableElement().style.left = '0%';
        this.mrs.getResizableElement().style.width = '100%';
        this.mrs.getResizableElement().style.height = '100%';
    }

    private hideOverlay() {
        this.mrs.getResizableElement().style.display = 'none';
        document.getElementById('top').style.display = 'none';
        document.getElementById('left').style.display = 'none';
        document.getElementById('right').style.display = 'none';
        document.getElementById('bottom').style.display = 'none';
    }

    private showOverlay() {
        this.mrs.getResizableElement().style.display = 'block';
        document.getElementById('top').style.display = 'block';
        document.getElementById('left').style.display = 'block';
        document.getElementById('right').style.display = 'block';
        document.getElementById('bottom').style.display = 'block';
    }

    public handleResizeEvent() {
        this.getContainer().bestFitContainerAndCanvas();
        this.mrs.init(this.valueChanges);
        this.showOverlay();
        // TODO FATAL INFINIT LOOP
        this.container.notify('resize');
        this.fitMovableResizableSqureToCanvas();
        this.mrs.update_faded_area();
        let dim = {
            width: this.mrs.getArea().width,
            height: this.mrs.getArea().height,
        };
        // Setting width_percentage and height_percentage to 100 is a hack
        const nextDim = Object.assign({}, this.mrs.getDim(), {
            width_percentage: 100,
            height_percentage: 100,
            isTrusted: false,
        })
        this.originalDim = nextDim;
        this.mrs.setDim(nextDim);
        this.valueChanges.next(nextDim);
    }

    apply() {
        const dim: Dimension2D = this.resizeFormGroup.value;
        // Last line of defence: never bake a non-finite or sub-1px size into the canvases —
        // a 0×0 resize destroys the image and kills the editor's layout math.
        if (!isFinite(dim.width) || !isFinite(dim.height) || dim.width < 1 || dim.height < 1) {
            return;
        }
        if (dim.width !== this.photoModel.getEphemeralCanvas().width ||
            dim.height !== this.photoModel.getEphemeralCanvas().height) {
            CCanvas.copyCanvas(this.photoModel.getPersistedCanvas(), this.photoModel.getEphemeralCanvas(), dim);
            CCanvas.resize(this.photoModel.getPersistedCanvas(), dim);
            CCanvas.copyCanvas(this.photoModel.getEphemeralCanvas(), this.mrs.getOverlayCanvas());

            // Apply filter
            // CCanvas.applyFilter(this.photoModel.getCarmanCanvas(), FilterConfig.getCurrentFilterConfig(), this.photoModel.getEphemeralCanvas());
            // CCanvas.applyFilter(this.photoModel.getCarmanCanvas(), FilterConfig.getCurrentFilterConfig(), this.mrs.getOverlayCanvas());

            this.handleResizeEvent();
        }
    }

    public async updateYImage(): Promise<HTMLImageElement> {
        return null;
    }

    public async finalize(): Promise<void> {
        const yImage = await this.updateYImage();
        // Driver.showLoading();
        this.mrs.getResizableElement().style.background = '';
        this.hideOverlay();

        let canvas = this.mrs.getOverlayCanvas();
        HTMLUtils.css(canvas, 'display', `none`);
        this.mrs.remove();
        if (this.valueChanges) {
            this.valueChanges.complete();
        }
        // if($("#resize .width").spinner){
        //    $("#resize .width").spinner("destroy");
        //    $("#resize .height").spinner("destroy");
        // }
        // Remove events
        let event = null;
        while (event = this.events.pop()) {
            this.container.removeEventListener(event);
        }

        RxUtils.safeCloseSubscriber(this.subscription);
    }
}
